import { useEffect, useState } from 'react';
import { api, money } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';

/* ------------------------------------------------------- client settings */

/**
 * BIGINT cents (a string from the server) -> "1000.00", for seeding an amount
 * field. BigInt, not Number: the same reason money.js exists at all. Display
 * only — nothing here turns a typed amount back into cents, the route does
 * that with toCents().
 */
function centsToRand(c) {
  if (c === null || c === undefined || c === '') return '';
  const v = BigInt(c);
  const neg = v < 0n;
  const a = neg ? -v : v;
  return `${neg ? '-' : ''}${a / 100n}.${String(a % 100n).padStart(2, '0')}`;
}

/** The details a tax invoice needs. Missing ones block the invoice, so say so here. */
export function EntitySettings({ entity, onSaved }) {
  const [form, setForm] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!entity?.id) return;
    api.get(`/api/entities/${entity.id}`).then((d) => setForm({
      name: d.entity.name ?? '', tradingName: d.entity.trading_name ?? '',
      registrationNumber: d.entity.registration_number ?? '',
      isVatRegistered: d.entity.is_vat_registered,
      vatNumber: d.entity.vat_number ?? '', vatCategory: d.entity.vat_category ?? 'B',
      addressLine1: d.entity.address_line1 ?? '', addressLine2: d.entity.address_line2 ?? '',
      city: d.entity.city ?? '', province: d.entity.province ?? '',
      postalCode: d.entity.postal_code ?? '', country: d.entity.country ?? 'South Africa',
      email: d.entity.email ?? '', phone: d.entity.phone ?? '',
      invoiceFooter: d.entity.invoice_footer ?? '',
      autoPostEnabled: d.entity.auto_post_enabled ?? false,
      autoPostCeiling: centsToRand(d.entity.auto_post_ceiling_cents),
    })).catch((e) => setErr(e.message));
  }, [entity?.id]);

  if (!form) return <div className="empty">Loading…</div>;

  const set = (k) => (e) => setForm({
    ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      // A blank limit means "leave the one that is there", not "clear it" —
      // the column is NOT NULL and toCents() rightly refuses an empty string.
      const payload = { ...form };
      if (!String(payload.autoPostCeiling ?? '').trim()) delete payload.autoPostCeiling;
      await api.patch(`/api/entities/${entity.id}`, payload);
      setMsg('Saved.');
      onSaved?.();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // The exact fields s.20(4) requires of the supplier.
  const gaps = [
    !form.addressLine1 && !form.city ? 'a street address or town' : null,
    form.isVatRegistered && !form.vatNumber ? 'a VAT registration number' : null,
  ].filter(Boolean);

  return (
    <>
      <ReportHead entity={entity} title="Client settings"
                  sub="These details appear on every tax invoice this client issues." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      {gaps.length > 0 && (
        <div className="note note--warn">
          <strong>Tax invoices are blocked until this is filled in.</strong> The VAT Act requires
          the supplier's name, address and VAT number on a tax invoice. Still missing: {gaps.join(' and ')}.
        </div>
      )}

      <h2 className="eyebrow" style={{ margin: '22px 0 10px' }}>Identity</h2>
      <div className="form-grid">
        <label className="field"><span>Registered name</span>
          <input value={form.name} onChange={set('name')} /></label>
        <label className="field"><span>Trading as</span>
          <input value={form.tradingName} onChange={set('tradingName')} placeholder="If different" /></label>
        <label className="field"><span>Registration number</span>
          <input className="input--figure" value={form.registrationNumber} onChange={set('registrationNumber')} /></label>
      </div>

      <h2 className="eyebrow" style={{ margin: '22px 0 10px' }}>Address</h2>
      <div className="form-grid">
        <label className="field"><span>Street address</span>
          <input value={form.addressLine1} onChange={set('addressLine1')} /></label>
        <label className="field"><span>Address line 2</span>
          <input value={form.addressLine2} onChange={set('addressLine2')} /></label>
        <label className="field"><span>Town or city</span>
          <input value={form.city} onChange={set('city')} /></label>
        <label className="field"><span>Province</span>
          <input value={form.province} onChange={set('province')} /></label>
        <label className="field"><span>Postal code</span>
          <input className="input--figure" value={form.postalCode} onChange={set('postalCode')} /></label>
        <label className="field"><span>Country</span>
          <input value={form.country} onChange={set('country')} /></label>
      </div>

      <h2 className="eyebrow" style={{ margin: '22px 0 10px' }}>Contact</h2>
      <div className="form-grid">
        <label className="field"><span>Email</span>
          <input type="email" value={form.email} onChange={set('email')} /></label>
        <label className="field"><span>Telephone</span>
          <input value={form.phone} onChange={set('phone')} /></label>
      </div>

      <h2 className="eyebrow" style={{ margin: '22px 0 10px' }}>VAT</h2>
      <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input type="checkbox" checked={form.isVatRegistered} onChange={set('isVatRegistered')}
               style={{ width: 16, height: 16 }} />
        <span style={{ letterSpacing: 0, textTransform: 'none', fontSize: 14, color: 'var(--ink)' }}>
          Registered for VAT
        </span>
      </label>
      {form.isVatRegistered && (
        <div className="form-grid">
          <label className="field"><span>VAT number</span>
            <input className="input--figure" value={form.vatNumber} onChange={set('vatNumber')} /></label>
          <label className="field"><span>VAT category</span>
            <select value={form.vatCategory} onChange={set('vatCategory')}>
              <option value="A">A — two-monthly, ending odd months</option>
              <option value="B">B — two-monthly, ending even months</option>
              <option value="C">C — monthly</option>
              <option value="D">D — six-monthly (farming)</option>
              <option value="E">E — annual</option>
            </select></label>
        </div>
      )}

      {/*
        The only place auto-posting can be switched on. Off by default, and
        off for every client until a practice decides otherwise: nothing in
        this system starts posting unattended on the day it is deployed.

        The limit is money, so it is typed as printed and converted server-side
        by toCents() like every other amount here.
      */}
      <h2 className="eyebrow" style={{ margin: '22px 0 10px' }}>Automatic posting</h2>
      <div className="note">
        A slip posts itself only when EVERY check passes: this switch is on, the model read the
        slip cleanly, its figures tie, a confirmed allocation rule matches the supplier, the period
        is open, it is not a duplicate, and its input VAT is claimable. Anything else goes to the
        review queue with a reason. A slip held back costs half a minute; a slip posted wrongly
        costs a reversal an auditor can see forever.
      </div>
      <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, margin: '12px 0' }}>
        <input type="checkbox" checked={form.autoPostEnabled} onChange={set('autoPostEnabled')}
               style={{ width: 16, height: 16 }} />
        <span style={{ letterSpacing: 0, textTransform: 'none', fontSize: 14, color: 'var(--ink)' }}>
          Let clean slips post without review
        </span>
      </label>
      {form.autoPostEnabled && (
        <div className="form-grid">
          <label className="field"><span>Only up to</span>
            <input className="input--figure" inputMode="decimal" placeholder="1000.00"
                   value={form.autoPostCeiling} onChange={set('autoPostCeiling')} /></label>
        </div>
      )}

      <h2 className="eyebrow" style={{ margin: '22px 0 10px' }}>Invoice footer</h2>
      <textarea rows={3} style={{ width: '100%' }} value={form.invoiceFooter}
                onChange={set('invoiceFooter')}
                placeholder="Banking details, payment terms — printed under the totals" />

      <button className="btn" style={{ marginTop: 16 }} onClick={save} disabled={busy}>
        {busy ? 'Saving' : 'Save settings'}
      </button>
    </>
  );
}

/* ------------------------------------------------------- opening balances */

/**
 * The takeover trial balance.
 *
 * Onboarding is what kills accounting software, so this takes the previous
 * system's closing figures and nothing else. Whatever does not balance goes to
 * retained earnings, which is what an accountant expects — a takeover trial
 * balance that balanced exactly would mean retained earnings had already been
 * reconciled, and it usually has not.
 */
export function OpeningBalances({ entity }) {
  const [accounts, setAccounts] = useState([]);
  const [date, setDate] = useState('');
  const [values, setValues] = useState({});
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!entity?.id) return;
    setDate(entity.financial_year_start ?? '');
    api.get(`/api/entities/${entity.id}/accounts`)
      .then((d) => setAccounts(d.accounts.filter((a) => a.is_postable)))
      .catch((e) => setErr(e.message));
  }, [entity?.id]);

  const toCents = (v) => {
    const m = /^(-?)(\d*)(?:\.(\d{0,2}))?$/.exec(String(v ?? '').trim());
    if (!m || (!m[2] && !m[3])) return 0n;
    const c = BigInt(m[2] || '0') * 100n + BigInt(((m[3] ?? '') + '00').slice(0, 2));
    return m[1] === '-' ? -c : c;
  };
  const fmt = (c) => {
    const neg = c < 0n; const a = neg ? -c : c;
    return `${neg ? '-' : ''}${a / 100n}.${String(a % 100n).padStart(2, '0')}`;
  };

  const total = Object.values(values).reduce((s, v) => s + toCents(v), 0n);
  const entered = Object.entries(values).filter(([, v]) => toCents(v) !== 0n);

  const submit = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const balances = Object.fromEntries(entered.map(([code, v]) => [code, v]));
      const r = await api.post(`/api/entities/${entity.id}/opening-balances`, { date, balances });
      setMsg(`Posted as entry ${r.entry.entryNo}.`);
      setValues({});
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const retained = accounts.find((a) => a.control_kind === 'retained_earnings');

  return (
    <>
      <ReportHead entity={entity} title="Opening balances"
                  sub="The closing figures from the previous system. Debits positive, credits negative." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <div className="controls">
        <label className="field"><span>Balances as at</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <button className="btn" onClick={submit} disabled={busy || !date || entered.length === 0}>
          {busy ? 'Posting' : 'Post opening balances'}
        </button>
      </div>

      <div className="scroll-x">
        <table className="ledger">
          <thead>
            <tr><th style={{ width: 70 }}>Code</th><th>Account</th>
                <th className="num" style={{ width: 190 }}>Balance</th></tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id}>
                <td className="code figure">{a.code}</td>
                <td>{a.name}</td>
                <td className="num">
                  <input className="input--figure" inputMode="decimal" placeholder="0.00"
                         style={{ width: 170, textAlign: 'right' }}
                         value={values[a.code] ?? ''}
                         onChange={(e) => setValues({ ...values, [a.code]: e.target.value })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={`balance${total === 0n ? '' : ' balance--off'}`}>
        <span className="balance__state">{total === 0n ? 'Balanced' : 'Difference'}</span>
        <span className="balance__detail">
          {total === 0n
            ? <>The trial balance you have entered balances exactly.</>
            : <>
                <strong>{money(fmt(total))}</strong> will be posted to{' '}
                {retained ? `${retained.code} ${retained.name}` : 'retained earnings'}.
                That is normal for a takeover — check it is the figure you expect.
              </>}
        </span>
      </div>
    </>
  );
}

/* --------------------------------------------------------------- periods */

export function Periods({ entity }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = () => api.get(`/api/entities/${entity.id}`).then(setData).catch((e) => setErr(e.message));
  useEffect(() => { if (entity?.id) load(); }, [entity?.id]);

  const setStatus = async (periodId, status) => {
    setErr(null); setMsg(null);
    try {
      const r = await api.post(`/api/entities/${entity.id}/periods/${periodId}/status`, { status });
      setMsg(`${r.period.label} is now ${r.period.status}.`);
      load();
    } catch (e) { setErr(e.message); }
  };

  if (!data) return <div className="empty">Loading…</div>;

  const BADGE = { open: 'Open', closed: 'Closed', locked: 'Locked' };

  return (
    <>
      <ReportHead entity={entity} title="Periods"
                  sub="Closing a period stops anything new being posted into it." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <div className="note">
        <strong>Closed</strong> can be reopened by an administrator. <strong>Locked</strong> cannot,
        ever — it represents a filed return or a signed year end. Corrections after a lock go into
        the next open period as a prior-period adjustment.
      </div>

      <h2 className="eyebrow" style={{ margin: '26px 0 10px' }}>Accounting periods</h2>
      <div className="scroll-x">
        <table className="ledger">
          <thead>
            <tr><th>Period</th><th>From</th><th>To</th><th>Status</th><th style={{ width: 220 }} /></tr>
          </thead>
          <tbody>
            {data.periods.map((p) => (
              <tr key={p.id}>
                <td>{p.label}</td>
                <td className="figure">{p.start_date}</td>
                <td className="figure">{p.end_date}</td>
                <td style={{ color: p.status === 'locked' ? 'var(--credit)' : p.status === 'closed' ? 'var(--ink-soft)' : 'var(--balanced)' }}>
                  {BADGE[p.status]}
                </td>
                <td className="num">
                  {p.status === 'open' && (
                    <>
                      <button className="icon-btn" onClick={() => setStatus(p.id, 'closed')}>Close</button>{' '}
                      <button className="icon-btn" onClick={() => setStatus(p.id, 'locked')}>Lock</button>
                    </>
                  )}
                  {p.status === 'closed' && (
                    <>
                      <button className="icon-btn" onClick={() => setStatus(p.id, 'open')}>Reopen</button>{' '}
                      <button className="icon-btn" onClick={() => setStatus(p.id, 'locked')}>Lock</button>
                    </>
                  )}
                  {p.status === 'locked' && <span style={{ color: 'var(--ink-faint)', fontSize: 12 }}>Permanent</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.vatPeriods.length > 0 && (
        <>
          <h2 className="eyebrow" style={{ margin: '34px 0 10px' }}>VAT periods</h2>
          <div className="note">
            A separate calendar. A two-monthly VAT period spans two accounting periods, and they
            lock independently — filing a VAT201 does not close the month.
          </div>
          <div className="scroll-x" style={{ marginTop: 12 }}>
            <table className="ledger">
              <thead><tr><th>Period</th><th>From</th><th>To</th><th>Status</th></tr></thead>
              <tbody>
                {data.vatPeriods.map((v) => (
                  <tr key={v.id}>
                    <td>{v.label}</td>
                    <td className="figure">{v.start_date}</td>
                    <td className="figure">{v.end_date}</td>
                    <td>{BADGE[v.status]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
